// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

// --- Types ---

export type SftpLocation = components["schemas"]["SftpLocationResponse"];
export type SftpManualRun = components["schemas"]["SftpManualRunResponse"];
export type SftpManualRunStatus = SftpManualRun["status"];

export interface SftpLocationsResult {
  locations: SftpLocation[];
  count: number;
}

export interface SftpManualRunsResult {
  manualRuns: SftpManualRun[];
  count: number;
}

/** Statuses that still owe a replay — the "current and pending" half of the list. */
export const UNFINISHED_STATUSES: readonly SftpManualRunStatus[] = ["pending", "running"];

/**
 * How long a run may sit in `running` before it is worth a second look.
 *
 * A row stuck here can mean the pod died mid-replay: the job claims a request *before* doing the
 * work, deliberately, so a kill leaves the row stuck rather than re-firing the import on every
 * later tick, and there is no self-healing.
 *
 * But it is not proof of that. The job's 55-minute budget is checked *between* requests, never
 * inside one — a claimed replay runs to completion however long it takes, and a chain-wide replay
 * fans out to one import per active store, so hours is a legitimate duration. Nothing in the list
 * data separates the two cases, so this only ever earns a "worth checking", never a diagnosis.
 * Six hours is a guess biased towards silence; no measured duration for a real replay exists yet.
 */
const LONG_RUNNING_MS = 6 * 60 * 60 * 1000;

export function isLongRunning(run: SftpManualRun): boolean {
  if (run.status !== "running" || !run.startedAt) return false;
  return Date.now() - new Date(run.startedAt).getTime() > LONG_RUNNING_MS;
}

// --- Location catalogue ---

/**
 * The locations the API will accept, mirrored server-side from the sftp-importer deploy config.
 * There is no other registry: `locationName` is a config string with no table behind it, so this
 * endpoint is the only thing that knows which feeds exist and which chain each one serves.
 */
export function useSftpLocationsQuery(params: { chainName?: string; enabled?: boolean } = {}) {
  const { chainName, enabled } = params;

  return useQuery({
    queryKey: ["sftp-locations", "list", { chainName }],
    enabled,
    // The catalogue only moves when someone edits the deploy config and ships the back office.
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const res = await api.GET("/v1/ingress/sftp-locations", {
        params: { query: { "filter[chainName]": chainName || undefined } },
      });
      return res.data as unknown as SftpLocationsResult;
    },
  });
}

// --- Manual run list ---

interface SftpManualRunsParams {
  chainName?: string;
  storeId?: number;
  locationName?: string;
  status?: SftpManualRunStatus;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: "id" | "locationName" | "importDate" | "chainId" | "storeId" | "status" | "createdAt" | "processedAt";
  orderBy?: "asc" | "desc";
  enabled?: boolean;
  refetchInterval?: number | false;
}

/**
 * Note on scoping a *store*: `filter[storeId]` matches store-scoped rows only, because a chain-wide
 * row has no `storeId` to match on. A store page therefore queries by `chainName` and splits the
 * result client-side — see `partitionRunsForStore`.
 */
export function useSftpManualRunsQuery(params: SftpManualRunsParams = {}) {
  const {
    chainName,
    storeId,
    locationName,
    status,
    search,
    page = 1,
    limit = 50,
    sortBy = "createdAt",
    orderBy = "desc",
    enabled,
    refetchInterval,
  } = params;

  return useQuery({
    queryKey: [
      "sftp-manual-runs",
      "list",
      { chainName, storeId, locationName, status, search, page, limit, sortBy, orderBy },
    ],
    enabled,
    refetchInterval,
    queryFn: async () => {
      const res = await api.GET("/v1/ingress/sftp-manual-runs", {
        params: {
          query: {
            page,
            limit,
            sortBy,
            orderBy,
            search: search || undefined,
            "filter[chainName]": chainName || undefined,
            "filter[storeId]": storeId ?? undefined,
            "filter[locationName]": locationName || undefined,
            "filter[status]": status || undefined,
          },
        },
      });
      return res.data as unknown as SftpManualRunsResult;
    },
  });
}

/**
 * Split a chain's runs into the ones that name this store and the chain-wide ones that cover it
 * anyway. Both are relevant on a store page and both need to be visible, but they are not the same
 * claim — a chain-wide row reaches every other store on the feed too.
 */
export function partitionRunsForStore(runs: SftpManualRun[], storeId: number) {
  return {
    storeScoped: runs.filter((run) => run.storeId === storeId),
    chainWide: runs.filter((run) => run.storeId == null),
  };
}

// --- Create ---

export interface CreateSftpManualRunParams {
  locationName: string;
  /** `yyyy-MM-dd`. Today or any past day; the backend rejects the future. */
  importDate: string;
  /** Omit for a chain-wide replay covering every store the feed serves. */
  storeId?: number;
  reason?: string;
  /**
   * Also queue every day after `importDate`, up to and including today — one row per day, drained
   * oldest first. Days already queued for the same target are skipped; only an all-queued range 409s.
   */
  untilToday?: boolean;
}

/**
 * Reasons a create can be refused, keyed off the status code plus the `detail` prefix the backend
 * stamps on each one. Mapped to translation keys rather than surfaced raw: the details are
 * machine-shaped (`store:4213 does not belong to chain:boots`) and operator-facing.
 */
export type SftpManualRunErrorCode =
  | "unknownLocation"
  | "locationNotReplayable"
  | "unknownStore"
  | "storeChainMismatch"
  | "futureDate"
  | "duplicate"
  | "unknown";

export class SftpManualRunError extends Error {
  readonly code: SftpManualRunErrorCode;
  readonly detail?: string;

  constructor(code: SftpManualRunErrorCode, detail?: string) {
    super(detail ?? code);
    this.name = "SftpManualRunError";
    this.code = code;
    this.detail = detail;
  }
}

function classifyCreateError(status: number, detail: string | undefined): SftpManualRunErrorCode {
  if (!detail) return "unknown";
  if (detail.startsWith("sftp_import_manual_run:")) return "duplicate";
  if (detail.startsWith("import_date:")) return "futureDate";
  if (detail.startsWith("sftp_location:")) {
    // Same prefix for both location failures — the status is what separates "no such feed" from
    // "that feed exists but replaying it is a no-op".
    return status === 404 ? "unknownLocation" : "locationNotReplayable";
  }
  if (detail.startsWith("store:")) {
    return status === 404 ? "unknownStore" : "storeChainMismatch";
  }
  return "unknown";
}

/**
 * Queues a replay. This does **not** start an import: it inserts a `pending` row that the
 * location's next scheduled cronjob tick picks up, reading the requested day instead of today.
 */
export function useCreateSftpManualRunMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateSftpManualRunParams) => {
      const res = await api.POST("/v1/ingress/sftp-manual-runs", {
        body: {
          locationName: params.locationName,
          importDate: params.importDate,
          storeId: params.storeId,
          reason: params.reason || undefined,
          untilToday: params.untilToday ?? false,
        },
      });
      if (res.error) {
        const errObj = res.error as { errors?: Array<{ detail?: string }> } | undefined;
        const detail = errObj?.errors?.find((e) => e?.detail)?.detail;
        throw new SftpManualRunError(classifyCreateError(res.response.status, detail), detail);
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sftp-manual-runs"] });
    },
  });
}
